/**
 * Per-tile-type static HTML builders for the interactive HTML snapshot
 * (S15). Pure — no React, no store reads. Reuses the SAME pure logic the
 * live tile views use (`resolveStatValue`/`computeStatCompare`,
 * `thresholdEval`, `pivotTransform`, `transformChartData`,
 * `analyzeColumns`/`getYAxisCandidates`) so a snapshot's numbers match what
 * the dashboard showed at export time.
 *
 * Chart tiles render as a single generic grouped-bar SVG (native `<title>`
 * hover tooltips, zero JS) rather than a full per-chart-type reproduction —
 * "interactive" per the plan means hover tooltips + local sort + a read-only
 * parameter strip, not Recharts-parity fidelity in an offline file. Pivot
 * tiles are NOT locally sortable (out of the plan's "local sort on table
 * tiles" scope) — they render the same aggregated grid the live tile shows.
 */
import type { QueryResultSet } from '../../../../types/queryExecution';
import { DEFAULT_CHART_COLORS } from '../../charts/types/chartTypes';
import { analyzeColumns } from '../../charts/utils/columnTypeDetection';
import { transformChartData } from '../../charts/utils/chartDataShaping';
import { resolveChartConfigColumnRefs } from '../../charts/utils/chartColumnRefs';
import type { ChartTile, MapTile, NoteTile, PivotTile, StatTile } from '../types/dashboardTypes';
import { pivotTransform, resolvePivotTileRefs } from '../utils/pivotTransform';
import { computeStatCompare, resolveStatTileRefs, resolveStatValue } from '../utils/statFormat';
import { thresholdEval } from '../utils/thresholdEval';
import { escapeHtml, HTML_SNAPSHOT_ROW_CAP } from './htmlSnapshotAssets';
import type { QueryBackedTile } from './tileExecutionService';

const EMPTY = (text: string) => `<div class="snap-empty">${escapeHtml(text)}</div>`;

const rowsBanner = (shown: number, total: number): string =>
  `<div class="snap-banner">Showing ${shown.toLocaleString()} of ${total.toLocaleString()} rows — truncated for this snapshot.</div>`;

const formatCellDisplay = (raw: unknown): string => {
  if (raw === null || raw === undefined) return '∅';
  if (raw instanceof Date) return raw.toISOString();
  return String(raw);
};

const isNumericCell = (raw: unknown): boolean =>
  typeof raw === 'number' || (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw)));

/** Table tile body — the ONE surface with local (client-JS) sort. */
export function buildTableTileHtml(result: QueryResultSet): string {
  const total = result.rows.length;
  const capped = result.rows.slice(0, HTML_SNAPSHOT_ROW_CAP);
  if (capped.length === 0) return EMPTY('No data');
  const cols = result.columns;
  const thead = cols.map((c, i) => `<th data-sort-idx="${i}">${escapeHtml(c.name)}</th>`).join('');
  const body = capped
    .map((row) => {
      const cells = cols
        .map((c) => {
          const key = c.id ?? c.name;
          const raw = row[key];
          const display = escapeHtml(formatCellDisplay(raw));
          if (raw !== null && raw !== undefined && isNumericCell(raw)) {
            return `<td data-value="${escapeHtml(String(raw))}">${display}</td>`;
          }
          return `<td>${display}</td>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');
  const banner = total > HTML_SNAPSHOT_ROW_CAP ? rowsBanner(capped.length, total) : '';
  return `${banner}<table class="snap-table" data-sortable><thead><tr>${thead}</tr></thead><tbody>${body}</tbody></table>`;
}

/** Pivot tile body — reuses the live grid's own aggregation, read-only. */
export function buildPivotTileHtml(tile: PivotTile, result: QueryResultSet): string {
  const total = result.rows.length;
  const capped = result.rows.slice(0, HTML_SNAPSHOT_ROW_CAP);
  // Same resolution seam `PivotTileView`/the add-tile preview use — an older
  // tile's NAME-based (or whitespace-rekeyed) column reference reads the
  // right column in the exported snapshot too (PR review, P2).
  const resolved = resolvePivotTileRefs(tile, analyzeColumns(result.columns, result.rows));
  const pivot = pivotTransform(capped, { ...resolved, aggregation: tile.aggregation });
  if (pivot.rows.length === 0) return EMPTY('No data');
  const cell = (v: number | null) => (v === null ? '∅' : v.toLocaleString());
  const head =
    `<th>${escapeHtml(tile.rowColumn)}</th>` +
    pivot.columnKeys.map((c) => `<th>${escapeHtml(c)}</th>`).join('') +
    '<th>Total</th>';
  const bodyRows = pivot.rows
    .map(
      (row) =>
        `<tr><th>${escapeHtml(row.rowKey)}</th>${row.cells
          .map((c) => `<td>${cell(c)}</td>`)
          .join('')}<td>${row.total.toLocaleString()}</td></tr>`,
    )
    .join('');
  const foot =
    `<tr><th>Total</th>${pivot.columnTotals
      .map((t) => `<td>${t.toLocaleString()}</td>`)
      .join('')}<td>${pivot.grandTotal.toLocaleString()}</td></tr>`;
  const banners =
    (total > HTML_SNAPSHOT_ROW_CAP ? rowsBanner(capped.length, total) : '') +
    (pivot.truncated
      ? '<div class="snap-banner">Showing the first 50 distinct columns — more exist for this data.</div>'
      : '');
  return `${banners}<table class="snap-table"><thead><tr>${head}</tr></thead><tbody>${bodyRows}</tbody><tfoot>${foot}</tfoot></table>`;
}

/** Stat tile body — same value/compare/threshold pipeline as StatTileView. */
export function buildStatTileHtml(tile: StatTile, result: QueryResultSet): string {
  const row = result.rows[0];
  // Same resolution seam `StatTileView`/`alertValuePreview` use — an older
  // tile's NAME-based (or whitespace-rekeyed) `valueColumn`/`compare.column`
  // reads the right column in the exported snapshot too (PR review, P2).
  const columnInfos = analyzeColumns(result.columns, result.rows);
  const { valueColumn, compare: resolvedCompare } = resolveStatTileRefs(tile, columnInfos);
  if (!row || !valueColumn) return EMPTY(!row ? 'No data' : 'No numeric column configured for this query');
  const outcome = resolveStatValue(row, valueColumn, tile.format);
  if (!outcome.ok) return EMPTY('Missing value');
  const severity = thresholdEval(outcome.raw, tile.thresholds);
  const compare = computeStatCompare(outcome.raw, resolvedCompare, row);
  const compareHtml = compare.kind === 'delta' ? `<div class="snap-stat-compare">${escapeHtml(compare.text)}</div>` : '';
  const severityAttr = severity !== 'none' ? ` data-severity="${severity}"` : '';
  return `<div class="snap-stat-value"${severityAttr}>${escapeHtml(outcome.text)}</div>${compareHtml}`;
}

const MAX_CHART_CATEGORIES_WITH_LABELS = 12;

/** Chart tile body — generic grouped-bar SVG with native `<title>` tooltips. */
export function buildChartTileHtml(tile: ChartTile, result: QueryResultSet): string {
  const total = result.rows.length;
  const capped = result.rows.slice(0, HTML_SNAPSHOT_ROW_CAP);
  // Same resolution seam `useChartData` uses live — an older tile's
  // NAME-based (or whitespace-rekeyed) column reference shapes the right
  // column in the exported snapshot too (PR review, P2).
  const resolvedConfig = resolveChartConfigColumnRefs(
    tile.chartConfig,
    analyzeColumns(result.columns, result.rows),
  );
  const shaped = transformChartData(capped, resolvedConfig, 'unknown');
  if (shaped.data.length === 0 || shaped.seriesKeys.length === 0) return EMPTY('No chart data');

  const width = 480;
  const height = 160;
  const pad = 24;
  const max = Math.max(
    1,
    ...shaped.data.flatMap((d) => shaped.seriesKeys.map((k) => Number(d[k]) || 0)),
  );
  const catCount = shaped.data.length;
  const catWidth = (width - pad * 2) / catCount;
  const barGroupWidth = catWidth * 0.8;
  const barWidth = barGroupWidth / shaped.seriesKeys.length;

  const bars = shaped.data
    .flatMap((d, ci) =>
      shaped.seriesKeys.map((sk, si) => {
        const value = Number(d[sk]) || 0;
        const barHeight = max > 0 ? (value / max) * (height - pad * 2) : 0;
        const x = pad + ci * catWidth + catWidth * 0.1 + si * barWidth;
        const y = height - pad - barHeight;
        const color = DEFAULT_CHART_COLORS[si % DEFAULT_CHART_COLORS.length];
        const label = `${d[shaped.xDataKey]} · ${sk}: ${value.toLocaleString()}`;
        return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${Math.max(0, barWidth - 1).toFixed(1)}" height="${Math.max(0, barHeight).toFixed(1)}" fill="${color}"><title>${escapeHtml(label)}</title></rect>`;
      }),
    )
    .join('');

  const xLabels =
    catCount <= MAX_CHART_CATEGORIES_WITH_LABELS
      ? shaped.data
          .map((d, ci) => {
            const x = pad + ci * catWidth + catWidth / 2;
            const text = String(d[shaped.xDataKey] ?? '').slice(0, 10);
            return `<text x="${x.toFixed(1)}" y="${height - 6}" text-anchor="middle">${escapeHtml(text)}</text>`;
          })
          .join('')
      : '';

  const svg = `<svg class="snap-chart-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="${escapeHtml(tile.title)} chart">${bars}${xLabels}</svg>`;

  const legend =
    shaped.seriesKeys.length > 1
      ? `<div class="snap-chart-legend">${shaped.seriesKeys
          .map(
            (sk, si) =>
              `<span><span class="snap-legend-swatch" style="background:${DEFAULT_CHART_COLORS[si % DEFAULT_CHART_COLORS.length]}"></span>${escapeHtml(sk)}</span>`,
          )
          .join('')}</div>`
      : '';

  const banner = total > HTML_SNAPSHOT_ROW_CAP ? rowsBanner(capped.length, total) : '';
  return `${banner}${svg}${legend}`;
}

/**
 * Map tile body — a STATIC summary, deliberately not a map: rendering
 * geometry offline would need the (lazy) decode pipeline + a map library and
 * a basemap, and the snapshot's hard CSP allows ZERO network references.
 * Same row-count honesty as the other bodies; reuses the existing pinned
 * `snap-empty` styling (the snapshot CSS is hash-allow-listed).
 */
export function buildMapTileHtml(tile: MapTile, result: QueryResultSet): string {
  const total = result.rows.length;
  if (total === 0) return EMPTY('No data');
  const geometry = tile.geometryColumn ? ` (${tile.geometryColumn})` : '';
  return EMPTY(
    `${total.toLocaleString()} row${total === 1 ? '' : 's'} with geometry${geometry} — map tiles are rendered as a summary in HTML snapshots.`,
  );
}

export function buildNoteTileHtml(tile: NoteTile): string {
  return tile.markdown.trim() ? `<div class="snap-note">${escapeHtml(tile.markdown)}</div>` : EMPTY('Empty note');
}

/** The `.sql` a query-backed tile ran — never `connectionId`/`database`/`engine`. */
export function tileSourceSql(tile: QueryBackedTile): string {
  return tile.source.sql;
}

/** Dispatches to the per-type builder above for a query-backed tile that HAS a result. */
export function buildQueryTileBodyHtml(tile: QueryBackedTile, result: QueryResultSet): string {
  switch (tile.tileType) {
    case 'chart':
      return buildChartTileHtml(tile, result);
    case 'stat':
      return buildStatTileHtml(tile, result);
    case 'table':
      return buildTableTileHtml(result);
    case 'pivot':
      return buildPivotTileHtml(tile, result);
    case 'map':
      return buildMapTileHtml(tile, result);
  }
}
